/**
 * Create Ticket Form Component
 * Form for creating new support tickets with validation
 */

import { useState } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Paper,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Alert,
} from '@mui/material'
import {
  TicketType,
  TicketPriority,
  TicketTypeLabels,
  TicketPriorityLabels,
  SUPPORT_TICKET_TYPES,
  CreateTicketRequest,
} from '@api/types/tickets'
import { TicketTypeIcon } from './TicketTypeIcon'
import { TicketPriorityIndicator } from './TicketPriorityIndicator'
import { TicketPhotoPicker } from './TicketPhotos'
import { useTicketPhotos } from './useTicketPhotos'

interface CreateTicketFormProps {
  onSubmit: (request: CreateTicketRequest) => Promise<void>
  onCancel?: () => void
  initialType?: TicketType
  initialTargetType?: string
  initialTargetId?: string
  isSubmitting?: boolean
}

export function CreateTicketForm({
  onSubmit,
  onCancel,
  initialType,
  initialTargetType,
  initialTargetId,
  isSubmitting = false,
}: CreateTicketFormProps) {
  const [type, setType] = useState<TicketType>(initialType ?? TicketType.Question)
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.Medium)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const photos = useTicketPhotos()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!title.trim()) {
      setError('Title is required')
      return
    }

    if (!description.trim()) {
      setError('Description is required')
      return
    }

    if (photos.uploading) {
      setError('Wait for the photos to finish uploading')
      return
    }

    try {
      await onSubmit({
        type,
        priority,
        title: title.trim(),
        description: description.trim(),
        tags: tags.length > 0 ? tags : undefined,
        attachmentIds: photos.readyIds.length > 0 ? photos.readyIds : undefined,
      })

      // Reset form
      setTitle('')
      setDescription('')
      setTags([])
      setTagInput('')
      photos.clear()
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to create ticket')
    }
  }

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim()
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove))
  }

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Create Support Ticket
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Submit a bug report, feature request, or get help with your account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Ticket Type */}
        <FormControl fullWidth>
          <InputLabel>Ticket Type *</InputLabel>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as TicketType)}
            label="Ticket Type *"
            disabled={isSubmitting}
          >
            {/* Support types only. The enum also carries moderation reasons and location reports,
                which are filed from the thing being reported and arrive with it attached — offering
                "Vandalism" here invites a complaint about content to be filed as a question about
                the app, with nothing to say what it was about. */}
            {SUPPORT_TICKET_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TicketTypeIcon type={value as TicketType} size="small" />
                    {TicketTypeLabels[value as TicketType]}
                  </Box>
                </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priority */}
        <FormControl fullWidth>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
            label="Priority"
            disabled={isSubmitting}
          >
            {Object.entries(TicketPriority)
              .filter(([key]) => isNaN(Number(key)))
              .map(([key, value]) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TicketPriorityIndicator priority={value as TicketPriority} variant="icon" size="small" />
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Title */}
        <TextField
          fullWidth
          label="Title *"
          placeholder="Brief description of the issue"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isSubmitting}
          inputProps={{ maxLength: 200 }}
          helperText={`${title.length}/200 characters`}
        />

        {/* Description */}
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={10}
          label="Description *"
          placeholder="Provide detailed information about your issue or request"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          inputProps={{ maxLength: 2000 }}
          helperText={`${description.length}/2000 characters`}
        />

        {/* Tags */}
        <Box>
          <TextField
            fullWidth
            label="Tags (optional)"
            placeholder="Add tags to categorize your ticket"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagInputKeyPress}
            disabled={isSubmitting}
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={handleAddTag} disabled={!tagInput.trim() || isSubmitting}>
                  Add
                </Button>
              ),
            }}
          />
          {tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                  disabled={isSubmitting}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Photos (private to the people who can see the ticket) */}
        <TicketPhotoPicker photos={photos.photos} onAdd={photos.add} onRemove={photos.remove} disabled={isSubmitting} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={isSubmitting || photos.uploading || !title.trim() || !description.trim()}>
            {isSubmitting ? 'Creating...' : 'Create Ticket'}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
